import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Popover, PopoverTrigger, PopoverContent, PopoverAnchor } from '@/components/ui/Popover'
import RadioCircle from '@/components/ui/RadioCircle'
import { plan_index_path, plan_top_down_select_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import { formatRevenueCompact } from './formatters'
import { type OrgNode } from './planShared'

export interface SwitcherPlan {
  id: string
  name: string
  slug: string
  begin_date: string
  end_date: string
  currency: string | null
  revenue_target: number
  published: boolean
}

export interface PlanSwitcherData {
  selected_plan_id: string | null
  plans: SwitcherPlan[]
  org_nodes: OrgNode[]
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

function formatPeriod(begin: string, end: string): string {
  const fmt = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  return `${fmt(begin)} – ${fmt(end)}`
}

function PlanRow({
  plan,
  selected,
  onSelect,
}: {
  plan: SwitcherPlan
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-muted"
    >
      <RadioCircle active={selected} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">{plan.name}</span>
        <span className="block text-xs text-muted-foreground">{formatPeriod(plan.begin_date, plan.end_date)}</span>
      </span>
      <span className="text-xs tabular-nums text-muted-foreground">
        {formatRevenueCompact(plan.revenue_target, plan.currency)}
      </span>
    </button>
  )
}

function Section({
  title,
  plans,
  selectedId,
  onSelect,
}: {
  title: string
  plans: SwitcherPlan[]
  selectedId: string | null
  onSelect: (plan: SwitcherPlan) => void
}) {
  if (plans.length === 0) return null
  return (
    <div className="mb-1">
      <div className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {plans.map((p) => (
        <PlanRow key={p.id} plan={p} selected={p.id === selectedId} onSelect={() => onSelect(p)} />
      ))}
    </div>
  )
}

// IBKR-style working-plan chip: shows the session-selected plan, opens a
// picker listing every plan in the market. Selecting one stores it in the
// server session, then re-enters the current page (redirectPath), whose
// content derives from the session plan. Defaults to the plan index.
export default function PlanSwitcher({ data, redirectPath }: { data: PlanSwitcherData; redirectPath?: string }) {
  const market = useMarket()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const selected = data.plans.find((p) => p.id === data.selected_plan_id) ?? null

  const handleSelect = async (plan: SwitcherPlan) => {
    if (submitting) return
    setSubmitting(true)
    try {
      const res = await fetch(plan_top_down_select_path(market.slug), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ plan_id: plan.id }),
      })
      if (res.ok) {
        setOpen(false)
        router.visit(redirectPath ?? plan_index_path(market.slug))
        return
      }
    } catch {
      // fall through to re-enable the picker
    }
    setSubmitting(false)
  }

  if (data.plans.length === 0) return null

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="flex items-center gap-3">
          {selected && (
            <>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/20"
              >
                {selected.name}
              </button>
              <span aria-hidden className="h-4 w-px bg-border" />
            </>
          )}
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
            >
              Select Plan
              <span aria-hidden className="text-[9px] leading-none opacity-70">▼</span>
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
        <PopoverContent align="start" className="w-72 p-1">
          <Section
            title="Active"
            plans={data.plans.filter((p) => p.published)}
            selectedId={data.selected_plan_id}
            onSelect={handleSelect}
          />
          <Section
            title="Draft"
            plans={data.plans.filter((p) => !p.published)}
            selectedId={data.selected_plan_id}
            onSelect={handleSelect}
          />
        </PopoverContent>
      </Popover>
    </>
  )
}
